import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Guid } from 'guid-typescript';
import { BehaviorSubject, forkJoin, lastValueFrom, Observable, Subject } from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs/operators';
import { Acl } from '../models/acl';
import { ImportReport } from '../models/import-report';
import { Property } from '../models/property';
import { User } from '../models/user';
import { IimportableService } from './iimportable-service';
import { OrganizationsService } from './organizations.service';
import { ServiceBase } from './service-base';
import { EnumUserAccessLevels } from '../models/enum-user-access-levels';
import { EnumOrganizationAccessLevels } from '../models/enum-organization-access-levels';

@Injectable({
    providedIn: 'root',
})
export class UsersService extends ServiceBase implements IimportableService {
    public currentUserSubject: BehaviorSubject<User>;

    public get currentUserChange$() {
        return this.currentUserSubject.asObservable();
    }

    public devAccount = false;

    private importBatch: number = 10;

    constructor(
        private http: HttpClient,
        private organizationsServices: OrganizationsService
    ) {
        super();
        this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(sessionStorage.getItem('user')));
    }

    /**
     * Sujet pour indiquer si l'import est terminé
     */
    private importFinished = new Subject<ImportReport>();

    /**
     * Propriété pour indiquer si l'import est terminé
     */
    public get onImportFinished$() {
        return this.importFinished.asObservable();
    }

    public getSessionCurrentUser(): Observable<User> {
        return this.currentUserSubject.pipe(first());
    }

    public isDevAccount(): boolean {
        return this.devAccount;
    }

    getCurrentUser(): Observable<User> {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.get<User>(`${this.baseUrl}/api/user`, httpOptions).pipe(
            tap((user: User) => {
                sessionStorage.setItem('user', JSON.stringify(user));
                this.currentUserSubject.next(user);

                if (user.id === 'c569a4b4eae74f99b31ca00f7b7de444') {
                    this.devAccount = true;
                } else {
                    this.devAccount = false;
                }
            })
        );
    }

    public checkCurrentUser(accessToken: string): Observable<User> {
        const httpOptions = {
            headers: new HttpHeaders().set('Authorization', accessToken).set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.get<User>(`${this.baseUrl}/api/user`, httpOptions).pipe(
            tap((user: User) => {
                sessionStorage.setItem('user', JSON.stringify(user));
                sessionStorage.setItem('SessionId', Guid.create().toString());
                this.currentUserSubject.next(user);

                if (user.id === 'c569a4b4eae74f99b31ca00f7b7de444') {
                    this.devAccount = true;
                } else {
                    this.devAccount = false;
                }
            })
        );
    }

    getUser(userId: string): Observable<User> {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.get<User>(`${this.baseUrl}/api/users/${userId}`, httpOptions).pipe(
            map((user) => {
                return user;
            })
        );
    }

    getUserWithDisplayParentOrganization(userId: string, executeOption: boolean = true): Observable<User> {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', !executeOption ? '' : this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.get<User>(`${this.baseUrl}/api/users/${userId}`, httpOptions).pipe(
            switchMap((user) =>
                this.organizationsServices.getOrganisation(user.organizationId).pipe(
                    map((organization) => {
                        user.displayParentOrganization = organization.display;

                        return user;
                    })
                )
            )
        );
    }

    getUsersWithDisplayParentOrganization(): Observable<User[]> {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return forkJoin([this.http.get<User[]>(`${this.baseUrl}/api/users`, httpOptions), this.organizationsServices.getOrganisations()]).pipe(
            map((res) => {
                const users = res[0];
                const organizations = res[1];

                users.forEach((user) => {
                    const displayParentOrg = organizations.find((x) => x.id === user.organizationId);
                    user.displayParentOrganization = displayParentOrg ? displayParentOrg.display : '';
                });

                return users;
            })
        );
    }

    getUsers(): Observable<User[]> {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.get<User[]>(`${this.baseUrl}/api/users`, httpOptions);
    }

    addUser(user: User): Observable<User> {
        const httpOptions = {
            headers: new HttpHeaders().set('Content-Type', 'application/json').set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.post<User>(`${this.baseUrl}/api/users`, JSON.stringify(user), httpOptions);
    }

    addUsers(users: User[], globalNotification = true): Observable<ImportReport> {
        const httpOptions = {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/json')
                .set('global-notification', String(globalNotification))
                .set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.post<ImportReport>(`${this.baseUrl}/api/users/import`, JSON.stringify(users), httpOptions);
    }

    deleteUser(userId: string) {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.delete(`${this.baseUrl}/api/users/${userId}`, httpOptions);
    }

    deleteUserProperty(userId: string, propertyName: string) {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.delete(`${this.baseUrl}/api/users/${userId}/properties/${propertyName}`, httpOptions);
    }

    deleteUserRight(userId: string, rightName: string) {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.delete(`${this.baseUrl}/api/users/${userId}/rights/${rightName}`, httpOptions);
    }

    updateUser(user: User): Observable<User> {
        const httpOptions = {
            headers: new HttpHeaders().set('Content-Type', 'application/json').set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.patch<User>(`${this.baseUrl}/api/users/${user.id}`, user, httpOptions);
    }

    updateUserProperty(userId: string, propertyName: string, propertyValue: string): Observable<Property> {
        const httpOptions = {
            headers: new HttpHeaders().set('Content-Type', 'application/json').set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.put<Property>(`${this.baseUrl}/api/users/${userId}/properties/${propertyName}`, JSON.stringify(propertyValue), httpOptions);
    }

    updateUserRight(userId: string, rightName: string, rightValue: number): Observable<Property> {
        const httpOptions = {
            headers: new HttpHeaders().set('Content-Type', 'application/json').set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.put<Property>(`${this.baseUrl}/api/users/${userId}/rights/${rightName}`, rightValue, httpOptions);
    }

    removeCurrentUser() {
        sessionStorage.clear();

        this.currentUserSubject.next(null);
        this.organizationsServices.removeCurrentOrganization();
    }

    async import(datas: any, properties: any, rights: any) {
        let usersToAdd: User[] = [];
        try {
            const datasToAdd = datas as User[];

            let propertiesUsers: { user: string; name: string; value: string }[]; //, readonly: boolean, legacyable: boolean}[];
            let rightsUsers: { user: string; name: string; value: number }[]; //, readonly: boolean, legacyable: boolean}[];

            if (properties) {
                propertiesUsers = properties as { user: string; name: string; value: string }[]; //, readonly: boolean, legacyable: boolean}[];
            }

            if (rights) {
                rightsUsers = rights as { user: string; name: string; value: number }[]; //, readonly: boolean, legacyable: boolean}[];
            }

            datasToAdd.forEach((user) => {
                let newUser: User = new User();
                // Force les valeurs en string au cas où c'est un nombre
                newUser.organizationId = this.organizationsServices.getCurrentOrganization().id; // user.organizationId ? String(user.organizationId) : '';
                newUser.account = user.account;
                newUser.display = user.display ? String(user.display).trim() : '';
                newUser.appData = user.appData ? String(user.appData).trim() : '';
                newUser.organizationAccessLevel = user.organizationAccessLevel ? user.organizationAccessLevel : EnumOrganizationAccessLevels.None;
                newUser.userAccessLevel = user.userAccessLevel ? user.userAccessLevel : EnumUserAccessLevels.None;
                newUser.networks = user.networks ? String(user.networks).trim() : '';
                newUser.enabled = Boolean(JSON.parse(String(user.enabled ?? 'true'))) ?? true;

                if (propertiesUsers) {
                    newUser.properties = propertiesUsers
                        .filter((p) => p.user === user.display)
                        .map((a) => {
                            let property = new Property();
                            property.name = a.name;
                            property.value = a.value ? String(a.value).trim() : '';

                            return property;
                        });
                }

                if (rightsUsers) {
                    newUser.rights = rightsUsers
                        .filter((r) => r.user === user.display)
                        .map((a) => {
                            let right = new Acl();
                            right.name = a.name.trim();
                            right.value = a.value;

                            return right;
                        });
                }

                usersToAdd.push(newUser);
            });

            let importReport: ImportReport = new ImportReport();
            importReport.progress.total = usersToAdd.length;

            this.importBatch = await lastValueFrom(this.getImportBatch());

            for (let index = 0; index < usersToAdd.length; index = index + this.importBatch) {
                let users: User[] = [];
                for (let num = index; num < index + this.importBatch && num < usersToAdd.length; num++) {
                    users.push(usersToAdd[num]);
                }

                try {
                    let report = await lastValueFrom(this.addUsers(users, false));

                    importReport.errorsReport = importReport.errorsReport.concat(report.errorsReport);
                    importReport.progress.completed += users.length;
                    if (importReport.progress.completed === importReport.progress.total) {
                        importReport.isFinished = true;
                    }
                    this.importFinished.next(importReport);
                } catch (e) {
                    importReport.isImportError = true;
                    importReport.isFinished = true;
                    console.log("Erreur à l'import: ", e);
                    this.importFinished.next(importReport);
                    break;
                }
            }
        } catch (e) {
            const fileError = new ImportReport();
            fileError.isFileTreatmentError = true;
            this.importFinished.next(fileError);
        }
    }

    private getImportBatch(): Observable<number> {
        return this.http.get<number>(`${this.baseUrl}/api/users/import/batchsetting`);
    }
}
